import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, Gift } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SHOWN_KEY = "dp_newsletter_seen";
const TRIGGER_SCROLL = 0.55; // show when 55% scrolled
const TRIGGER_DELAY = 25000; // or after 25s

export default function NewsletterSlideIn() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const shownRef = useRef(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (localStorage.getItem(SHOWN_KEY) === "1") {
      shownRef.current = true;
      return;
    }
    // Don't collide with ExitPopup
    if (localStorage.getItem("dp_exit_seen") === "1" && localStorage.getItem("dp_discount")) {
      shownRef.current = true;
      return;
    }

    const trigger = () => {
      if (shownRef.current) return;
      shownRef.current = true;
      setOpen(true);
      localStorage.setItem(SHOWN_KEY, "1");
    };

    const onScroll = () => {
      const ratio =
        window.scrollY / (document.body.scrollHeight - window.innerHeight);
      if (ratio > TRIGGER_SCROLL) trigger();
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    const t = setTimeout(trigger, TRIGGER_DELAY);

    return () => {
      window.removeEventListener("scroll", onScroll);
      clearTimeout(t);
    };
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    try {
      setLoading(true);
      const { data } = await axios.post(`${API}/leads`, {
        email,
        source: "newsletter_slidein",
      });
      if (data?.discount_code) {
        localStorage.setItem("dp_discount", data.discount_code);
        toast.success(`Code ${data.discount_code} saved · auto-applied at checkout`);
      }
      setOpen(false);
    } catch {
      toast.error("Could not subscribe. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="newsletter-slidein"
      className="fixed bottom-4 left-4 right-4 md:right-6 md:left-auto md:bottom-6 z-[90] max-w-sm md:w-[360px] bg-[#0e0e10] text-white rounded-3xl shadow-2xl p-5 animate-[slideIn_.45s_ease-out]"
    >
      <button
        type="button"
        onClick={() => setOpen(false)}
        aria-label="Close"
        data-testid="newsletter-close"
        className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 inline-flex items-center justify-center"
      >
        <X size={14} />
      </button>

      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold bg-[#d8f06b] text-[#0e0e10] px-2.5 py-1 rounded-full">
        <Gift size={12} /> Subscriber perk
      </div>
      <h4 className="mt-3 text-[20px] font-black leading-tight">
        First-time visitor?
        <br />
        Save <span className="text-[#d8f06b]">10% on us.</span>
      </h4>
      <p className="mt-1.5 text-[12px] text-white/70 leading-relaxed">
        Join 12,400+ sleepers. Drop your email — we&apos;ll save your code instantly.
      </p>

      <form onSubmit={submit} className="mt-3 flex gap-2">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          data-testid="newsletter-email"
          className="flex-1 bg-white/10 text-white placeholder-white/50 rounded-full px-4 py-2.5 text-[13px] focus:outline-none focus:bg-white/15"
        />
        <button
          type="submit"
          disabled={loading}
          data-testid="newsletter-submit"
          className="bg-[#d8f06b] text-[#0e0e10] font-extrabold text-[12px] uppercase tracking-wider rounded-full px-4 py-2.5 hover:scale-[1.02] transition disabled:opacity-60"
        >
          {loading ? "…" : "Get code"}
        </button>
      </form>
    </div>
  );
}
